"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Search, Link2, Map, ChartLine as LineChart, Smartphone, FileText, Activity, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Calendar } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const localServices = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  description: "Complete setup, primary category tuning, geotagged media updates, and attribute management to maximize your visibility in the Google 3-Pack.",
  badge: "Core Signal",
  deliverable1: "Full NAP audit & category alignment",
  deliverable2: "Weekly Q&A and post management",
  link: "/LocalSEOPage"
}, {
  id: "citation-building",
  title: "Local Citation Building & Cleanup",
  description: "Consistent NAP distribution across authoritative directories, resolving legacy errors and suppressing duplicate listings.",
  badge: "Authority",
  deliverable1: "50+ high-tier local submissions",
  deliverable2: "Duplicate listing suppression",
  link: "/LocalSEOPage"
}, {
  id: "review-mgmt",
  title: "Local Review Strategy & Management",
  description: "Ethical strategies to generate authentic customer reviews and build review velocity that strengthens local map pack positions.",
  badge: "Reputation",
  deliverable1: "Automated review request workflows",
  deliverable2: "Response strategy & keyword integration",
  link: "/LocalSEOPage"
}, {
  id: "local-schema",
  title: "Local Schema Markup & Structured Data",
  description: "Custom JSON-LD structured data implementation specifying LocalBusiness types, GeoCoordinates, and service boundaries.",
  badge: "Technical",
  deliverable1: "Granular LocalBusiness schema setup",
  deliverable2: "Geo-coordinates & address validation",
  link: "/TechnicalSeoPage"
}, {
  id: "geo-keywords",
  title: "On-Page Local Keywords & Geo-Targeting",
  description: "Strategic placement of local intent keywords, town and county references, and geo-targeted metadata across primary landing pages.",
  badge: "On-Page",
  deliverable1: "Geo-targeted H1-H3 & meta optimization",
  deliverable2: "Service area page keyword map",
  link: "/LocalSEOPage"
}, {
  id: "local-links",
  title: "Hyper-Local Link Building",
  description: "Securing contextual, location-relevant backlinks from regional news, local business associations, and trusted community partners.",
  badge: "Link Building",
  deliverable1: "Local sponsorship & partner outreach",
  deliverable2: "Regional editorial link acquisition",
  link: "/LocalSEOPage"
}, {
  id: "multi-location",
  title: "Multi-Location SEO Strategy",
  description: "Scalable site architecture and individual landing pages designed to capture local market share across multiple branches.",
  badge: "Enterprise",
  deliverable1: "Multi-branch location hierarchy",
  deliverable2: "Canonical & localized URL structure",
  link: "/LocalSEOPage"
}, {
  id: "competitor-analysis",
  title: "Local Competitor & SERP Analysis",
  description: "Deep dive into your top map pack competitors to uncover ranking gaps, link profiles, and review velocity metrics.",
  badge: "Strategy",
  deliverable1: "Proximity & map gap benchmarking",
  deliverable2: "Competitor backlink breakdown",
  link: "/LocalSEOPage"
}, {
  id: "voice-mobile",
  title: "Voice Search & Mobile Local Optimization",
  description: "Optimizing your web assets for mobile page speed, tap-to-call actions, and natural language queries like 'near me'.",
  badge: "UX & Mobile",
  deliverable1: "Conversational FAQ optimization",
  deliverable2: "Mobile UX & click-to-call tracking",
  link: "/LocalSEOPage"
}, {
  id: "localized-content",
  title: "Localized Content & Landing Pages",
  description: "Creating dedicated, high-converting service area pages tailored to local customer search intent and pain points.",
  badge: "Content",
  deliverable1: "City-specific landing page strategy",
  deliverable2: "Local CTA & conversion optimization",
  link: "/LocalSEOPage"
}, {
  id: "rank-tracking",
  title: "Local Rank Tracking & Map Monitoring",
  description: "Postcode-level geo-grid rank tracking so you can visualize true map pack positions across your entire target area.",
  badge: "Analytics",
  deliverable1: "13x13 postcode map grid tracking",
  deliverable2: "Monthly position movement logs",
  link: "/LocalSEOPage"
}, {
  id: "local-audit",
  title: "Local SEO Audit & Technical Health",
  description: "Comprehensive evaluation of local search barriers, indexing errors, map penalty risk, and actionable fix roadmaps.",
  badge: "Audit",
  deliverable1: "Full technical local SEO audit",
  deliverable2: "Prioritized implementation roadmap",
  link: "/SeoAuditsPage"
}];
const serviceIcons = [MapPin, Building2, Star, Code2, Search, Link2, Map, LineChart, Smartphone, FileText, Activity, ShieldCheck];
export default function Localservices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 transition-colors duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Hands-On Local SEO Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Specialized Local SEO Services for Regional Dominance
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When you work with me, you get direct expert execution—no junior account managers or generic AI templates. Here is how I optimize every local search signal to position your business at the top of Google Map Packs and local SERPs.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {localServices.map((service, i) => {
          const IconComponent = serviceIcons[i % serviceIcons.length];
          return <motion.div key={service.id} data-index={i} variants={cardVariants} className="flex flex-col h-full">
                <Card className="flex flex-col h-full overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 group rounded-xl p-0">
                  {/* Card Top / Header */}
                  <div className="bg-primary text-primary-foreground p-5 sm:p-6 flex flex-col justify-between gap-4 min-h-[110px]">
                    <div className="flex items-center justify-between gap-3">
                      <div className="p-2.5 rounded-lg bg-primary-foreground/10 text-primary-foreground shrink-0">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge className="bg-primary-foreground/20 text-primary-foreground border-transparent hover:bg-primary-foreground/30 text-xs font-medium">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg sm:text-xl font-bold font-serif leading-tight text-primary-foreground">
                      {service.title}
                    </CardTitle>
                  </div>

                  {/* Card Body */}
                  <CardContent className="p-5 sm:p-6 flex-1 flex flex-col justify-between gap-6">
                    <div>
                      <CardDescription className="text-sm text-card-foreground/80 font-sans leading-relaxed mb-6">
                        {service.description}
                      </CardDescription>

                      <Separator className="mb-4" />

                      {/* Deliverables List */}
                      <div className="space-y-2.5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Key Deliverables:
                        </p>
                        <div className="flex items-start gap-2 text-xs text-card-foreground/90 font-sans">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{service.deliverable1}</span>
                        </div>
                        <div className="flex items-start gap-2 text-xs text-card-foreground/90 font-sans">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{service.deliverable2}</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2">
                      <Button asChild variant="outline" className="w-full justify-between text-xs font-semibold hover:border-primary hover:text-primary transition-colors group/btn">
                        <Link to={service.link}>
                          <span>Explore {service.badge}</span>
                          <ArrowRight className="size-3.5 transition-transform group-hover/btn:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA / Strategy Call Strip */}
        <div className="mt-16 bg-muted/60 border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-xs">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-primary/10 text-primary mb-4">
            <SafeHydrate><Calendar className="size-6" /></SafeHydrate>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-3">
            Ready to Dominate Local Search in Your Region?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto mb-6 font-sans">
            Book a complimentary 30-minute local SEO strategy call directly with me. I will review your Google Business Profile and local search visibility before we speak.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold px-8 shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/CaseStudiesPage">
                View Local SEO Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}